import React from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plate } from "@/components/Plate";
import { toast } from "sonner";
import { CheckCircle, XCircle, Info, Trash } from "@phosphor-icons/react";
import { useLiveQuery } from "@/lib/liveSync";

// SEC-001: Müşteri plaka doğrulama kuyruğu. Adminin manuel onaylaması
// gerektiği için kritik güvenlik ekranı — başka biri size ait plakayı
// bildirirse burada görünür ve reddedebilirsiniz.
export default function PlateVerifications() {
  const { user: authUser } = useAuth();
  const isSuperAdmin = authUser?.role === "super_admin";
  const [status, setStatus] = React.useState("pending");
  const { data: items, refetch } = useLiveQuery(
    ["plate_verifications", status],
    async () => (await api.get(`/plate-verifications?status=${status}`)).data,
    { intervalMs: 15000 },
  );

  const act = async (id, action) => {
    if (action === "reject" && !window.confirm("Bu doğrulama isteğini reddetmek istediğinize emin misiniz?")) return;
    try {
      await api.post(`/plate-verifications/${id}/${action}`);
      toast.success(action === "approve" ? "Onaylandı" : "Reddedildi");
      refetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İşlem başarısız");
    }
  };

  const deleteRequest = async (r) => {
    if (!window.confirm(
      `Bu plaka doğrulama isteğini kalıcı olarak silmek istediğinize emin misiniz?\n\n` +
      `Plaka: ${r.plate}\nDurum: ${r.status}`
    )) return;
    try {
      await api.delete(`/plate-verifications/${r.id}`);
      toast.success("Silindi");
      refetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silinemedi");
    }
  };

  const rows = items || [];

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Güvenlik</div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Plaka Doğrulama Talepleri</h1>
        <div className="text-sm text-muted-foreground mt-1">
          Müşteri hesabına bir plakanın bağlanma talebi. Onaylanana kadar müşteri
          o aracın geçmişini göremez.
        </div>
      </div>

      <Card className="rounded-sm border-amber-200 bg-amber-50">
        <CardContent className="p-4 flex items-start gap-3">
          <Info size={20} weight="fill" className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-amber-900">
            <b>Güvenlik prosedürü:</b> Onaylamadan önce müşteri kimliğini
            (Türkiye ruhsatı vb.) manuel olarak kontrol edin. Yanlış onay,
            başka birinin aracına ait bilgilerin sızmasına yol açar.
          </div>
        </CardContent>
      </Card>

      <div className="flex gap-2">
        {["pending", "approved", "rejected"].map((s) => (
          <Button
            key={s}
            variant={status === s ? "default" : "outline"}
            onClick={() => setStatus(s)}
            data-testid={`pv-tab-${s}`}
            className="rounded-sm uppercase tracking-widest font-semibold text-xs"
          >
            {s === "pending" ? "Bekleyen" : s === "approved" ? "Onaylı" : "Red"}
          </Button>
        ))}
      </div>

      {rows.length === 0 ? (
        <Card className="rounded-sm border-border">
          <CardContent className="p-8 text-center text-muted-foreground text-sm">
            {status === "pending" ? "Bekleyen doğrulama talebi yok." :
             status === "approved" ? "Onaylanmış talep yok." : "Reddedilmiş talep yok."}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {rows.map((r) => (
            <Card key={r.id} className="rounded-sm border-border">
              <CardContent className="p-5 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">
                      Müşteri
                    </div>
                    <div className="font-semibold text-sm truncate">{r.email}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                      Talep: {new Date(r.created_at).toLocaleString("tr-TR")}
                    </div>
                  </div>
                  <Plate className="text-lg">{r.plate}</Plate>
                </div>
                {r.status === "pending" && (
                  <div className="flex gap-2 pt-2 border-t border-border">
                    <Button
                      onClick={() => act(r.id, "approve")}
                      data-testid={`pv-approve-${r.id}`}
                      className="flex-1 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white uppercase tracking-widest text-xs font-semibold"
                    >
                      <CheckCircle size={14} weight="fill" /> Onayla
                    </Button>
                    <Button
                      onClick={() => act(r.id, "reject")}
                      variant="outline"
                      data-testid={`pv-reject-${r.id}`}
                      className="flex-1 rounded-sm uppercase tracking-widest text-xs font-semibold text-destructive hover:text-destructive"
                    >
                      <XCircle size={14} weight="fill" /> Reddet
                    </Button>
                  </div>
                )}
                {r.status === "approved" && (
                  <div className="text-[11px] text-emerald-700 pt-2 border-t border-border flex items-center justify-between gap-2">
                    <span>✓ {r.approved_by} tarafından {new Date(r.approved_at).toLocaleString("tr-TR")} tarihinde onaylandı</span>
                    {isSuperAdmin && (
                      <Button variant="ghost" size="sm" onClick={() => deleteRequest(r)}
                              data-testid={`pv-delete-${r.id}`}
                              className="rounded-sm text-destructive h-7 w-7 p-0"
                              title="Süperadmin: Kaydı sil"><Trash size={12} /></Button>
                    )}
                  </div>
                )}
                {r.status === "rejected" && (
                  <div className="text-[11px] text-destructive pt-2 border-t border-border flex items-center justify-between gap-2">
                    <span>✗ {r.rejected_by} tarafından {new Date(r.rejected_at).toLocaleString("tr-TR")} tarihinde reddedildi</span>
                    {isSuperAdmin && (
                      <Button variant="ghost" size="sm" onClick={() => deleteRequest(r)}
                              data-testid={`pv-delete-${r.id}`}
                              className="rounded-sm text-destructive h-7 w-7 p-0"
                              title="Süperadmin: Kaydı sil"><Trash size={12} /></Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
